/* Generic cockpit report exporter.
   A "report model" (title, meta, sections of kpis / kv / tables / charts / notes) is
   rendered to HTML & PDF (print) & XLSX on the client, and to PPTX & DOCX on the backend
   (/api/nova/report-export). Charts are small SVGs built from data, rasterized to PNG for
   the backend. Used by the Molecule Cockpit (and reusable elsewhere). */
import * as XLSX from 'xlsx';

const C = { navy: '#0D2B3E', teal: '#1A8AAF', red: '#D64545', amber: '#D98323', green: '#1D8A5E', brd: '#D5E6EF', surf: '#F4F8FB', t3: '#6B8299' };
const PALETTE = ['1A8AAF', '0D2B3E', '2FA4C7', '1D8A5E', 'D98323', '6B8299', '8E5AA8', '3E7CB1'];
const AX = '#6B8299', GRID = '#EEF3F6', TXT = '#33525F';
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
const hx = (c) => '#' + String(c || '1A8AAF').replace('#', '');
const today = () => new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const safeName = (s) => String(s || 'report').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
const dateStamp = () => new Date().toISOString().slice(0, 10).replace(/-/g, '');
export const stripHtml = (s) => String(s || '').replace(/<[^>]+>/g, ' ').replace(/&[a-z]+;/g, ' ').replace(/\s+/g, ' ').trim();

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
function printHtml(html) {
  const w = window.open('', '_blank');
  if (!w) { alert('Please allow pop-ups to export as PDF, then use “Save as PDF” in the print dialog.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
  const go = () => { try { w.focus(); w.print(); } catch { /* ignore */ } };
  w.onload = go; setTimeout(go, 700);
}

/* ───── small SVG charts from data ───── */
const frame = (w, h, inner) => `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" font-family="Inter,Arial,sans-serif">${inner}</svg>`;
const emptySvg = (w, h) => frame(w, h || 80, `<text x="10" y="26" fill="${AX}" font-size="12">No data.</text>`);
export function svgBarsH(items, opts = {}) {
  items = (items || []).filter((d) => d && d.value != null);
  const w = opts.w || 640; if (!items.length) return emptySvg(w);
  const rowH = 24, padL = opts.padL || 190, padR = 50, top = 8, h = items.length * rowH + top + 8;
  const max = Math.max(...items.map((d) => +d.value || 0), 1), barW = w - padL - padR;
  let g = '';
  items.forEach((d, i) => {
    const y = top + i * rowH, bw = Math.max(2, ((+d.value || 0) / max) * barW), col = hx(opts.color || PALETTE[i % PALETTE.length]);
    g += `<text x="${padL - 8}" y="${y + 16}" text-anchor="end" font-size="11" fill="${TXT}">${esc(String(d.name).slice(0, 32))}</text>`;
    g += `<rect x="${padL}" y="${y + 5}" width="${bw}" height="14" rx="3" fill="${col}"/>`;
    g += `<text x="${padL + bw + 6}" y="${y + 16}" font-size="10" fill="${AX}">${esc(d.value)}</text>`;
  });
  return frame(w, h, g);
}
export function svgLine(points, opts = {}) {
  points = points || [];
  const w = opts.w || 640, h = opts.h || 260, key = opts.key || 'value', col = hx(opts.color || '1A8AAF');
  if (!points.length) return emptySvg(w, h);
  const padL = 48, padR = 16, padT = 12, padB = 26, iw = w - padL - padR, ih = h - padT - padB;
  const max = Math.max(...points.map((p) => +p[key] || 0), 1);
  let g = `<line x1="${padL}" y1="${padT}" x2="${padL}" y2="${h - padB}" stroke="${AX}"/><line x1="${padL}" y1="${h - padB}" x2="${w - padR}" y2="${h - padB}" stroke="${AX}"/>`;
  for (let k = 0; k <= 4; k++) { const v = Math.round(max * k / 4), y = h - padB - ih * k / 4; g += `<line x1="${padL}" y1="${y}" x2="${w - padR}" y2="${y}" stroke="${GRID}"/><text x="${padL - 6}" y="${y + 3}" text-anchor="end" font-size="9" fill="${AX}">${v}</text>`; }
  const px = (i) => padL + (points.length === 1 ? iw / 2 : iw * i / (points.length - 1));
  const xy = points.map((p, i) => [px(i), h - padB - ((+p[key] || 0) / max) * ih]);
  const line = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  if (opts.area) g += `<polygon points="${padL},${h - padB} ${line} ${padL + iw},${h - padB}" fill="${col}" opacity="0.15"/>`;
  g += `<polyline points="${line}" fill="none" stroke="${col}" stroke-width="2.5"/>`;
  xy.forEach(([x, y], i) => { g += `<circle cx="${x}" cy="${y}" r="3" fill="${col}"/>`; g += `<text x="${px(i)}" y="${h - padB + 15}" text-anchor="middle" font-size="9" fill="${TXT}">${esc(points[i].label || points[i].year)}</text>`; });
  return frame(w, h, g);
}
export function svgDonut(items, opts = {}) {
  items = (items || []).filter((d) => +d.value > 0);
  const w = opts.w || 440, h = opts.h || 240; if (!items.length) return emptySvg(w, h);
  const total = items.reduce((a, d) => a + (+d.value || 0), 0), cx = 130, cy = h / 2, rO = 92, rI = 54;
  let ang = -Math.PI / 2, g = '';
  items.forEach((d, i) => {
    const col = hx(d.color || PALETTE[i % PALETTE.length]);
    const a2 = ang + ((+d.value || 0) / total) * 2 * Math.PI;
    const x1 = cx + rO * Math.cos(ang), y1 = cy + rO * Math.sin(ang), x2 = cx + rO * Math.cos(a2), y2 = cy + rO * Math.sin(a2);
    const xi1 = cx + rI * Math.cos(a2), yi1 = cy + rI * Math.sin(a2), xi2 = cx + rI * Math.cos(ang), yi2 = cy + rI * Math.sin(ang);
    const large = (a2 - ang) > Math.PI ? 1 : 0;
    g += `<path d="M${x1} ${y1} A${rO} ${rO} 0 ${large} 1 ${x2} ${y2} L${xi1} ${yi1} A${rI} ${rI} 0 ${large} 0 ${xi2} ${yi2} Z" fill="${col}"/>`;
    ang = a2;
  });
  items.forEach((d, i) => { const ly = cy - items.length * 11 + i * 24; g += `<rect x="270" y="${ly - 11}" width="12" height="12" fill="${hx(d.color || PALETTE[i % PALETTE.length])}"/><text x="288" y="${ly}" font-size="11" fill="${TXT}">${esc(d.name)} (${esc(d.value)})</text>`; });
  return frame(w, h, g);
}

async function rasterizeSvg(svgString, scale = 2) {
  const w = +(svgString.match(/width="(\d+)"/) || [])[1] || 640;
  const h = +(svgString.match(/height="(\d+)"/) || [])[1] || 260;
  const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
  const img = new Image(); img.width = w; img.height = h;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const canvas = document.createElement('canvas'); canvas.width = w * scale; canvas.height = h * scale;
  const ctx = canvas.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, w * scale, h * scale);
  return { data: canvas.toDataURL('image/png'), w, h };
}

/* ───── HTML ───── */
function tableHtml(t) {
  if (!t || !t.rows || !t.rows.length) return '<div class="muted">No rows.</div>';
  const head = t.cols.map((c) => `<th>${esc(c.label)}</th>`).join('');
  const body = t.rows.map((r) => `<tr>${t.cols.map((c) => `<td>${esc(r[c.key])}</td>`).join('')}</tr>`).join('');
  return `${t.title ? `<h3>${esc(t.title)}</h3>` : ''}<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}
function sectionHtml(s) {
  let inner = '';
  if (s.kpis && s.kpis.length) inner += `<div class="kpis">${s.kpis.map((k) => `<div class="kpi"><div class="kl">${esc(k.label)}</div><div class="kv">${esc(k.value)}</div></div>`).join('')}</div>`;
  if (s.kv && s.kv.length) inner += `<div class="grid">${s.kv.map((x) => `<div><div class="k">${esc(x.k)}</div><div class="v">${esc(x.v)}</div></div>`).join('')}</div>`;
  (s.charts || []).forEach((c) => { inner += `${c.title ? `<h3>${esc(c.title)}</h3>` : ''}<div class="chart">${c.svg || ''}</div>`; });
  (s.tables || []).forEach((t) => { inner += tableHtml(t); });
  (s.notes || []).forEach((n) => { if (n) inner += `<div class="note">${esc(n)}</div>`; });
  return `<section><h2><span class="dot"></span>${esc(s.heading)}</h2>${inner}</section>`;
}
export function buildReportHtml(m) {
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(m.title)} — ${esc(m.subtitle || '')}</title><style>
*{box-sizing:border-box} body{font-family:Inter,Arial,sans-serif;margin:0;background:${C.surf};color:${C.navy};font-size:13px}
.wrap{max-width:1000px;margin:0 auto;padding:24px}
.hero{background:linear-gradient(135deg,${C.navy},${C.teal});color:#fff;border-radius:16px;padding:24px 26px;margin-bottom:18px}
.hero .w{font-size:11px;letter-spacing:.12em;text-transform:uppercase;opacity:.85;font-weight:600}
.hero h1{font-size:30px;margin:4px 0 6px} .hero .s{opacity:.9}
section{background:#fff;border:1px solid ${C.brd};border-radius:12px;padding:16px 18px;margin-bottom:16px}
h2{font-size:16px;margin:0 0 12px;display:flex;align-items:center;gap:8px}.dot{width:8px;height:8px;border-radius:50%;background:${C.teal};display:inline-block}
h3{font-size:13px;color:#33525f;margin:14px 0 8px}
.kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px}
.kpi{background:${C.surf};border:1px solid ${C.brd};border-radius:12px;padding:12px 14px}
.kl{font-size:11px;color:${C.t3};text-transform:uppercase;letter-spacing:.04em;font-weight:600}.kv{font-size:22px;font-weight:800;margin-top:2px}
.grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px 20px;margin:4px 0}
.grid .k{font-size:10.5px;color:${C.t3};text-transform:uppercase;letter-spacing:.04em;font-weight:600}.grid .v{margin-top:1px}
.chart{margin:6px 0}.chart svg{width:100%;height:auto;max-width:720px}
table{width:100%;border-collapse:collapse;font-size:11.5px;margin-top:6px}th{text-align:left;padding:6px 8px;border-bottom:2px solid ${C.brd};color:${C.t3};text-transform:uppercase;font-size:10px;letter-spacing:.04em}
td{padding:5px 8px;border-bottom:1px solid #e8f0f5}
.note{background:${C.surf};border-left:3px solid ${C.teal};border-radius:8px;padding:9px 12px;margin-top:10px;line-height:1.55}
.muted{color:${C.t3}}
@media print{body{background:#fff}section{break-inside:avoid}}
</style></head><body><div class="wrap">
<div class="hero"><div class="w">${esc(m.subtitle || 'Report')}</div><h1>${esc(m.title)}</h1><div class="s">${(m.meta || []).map((x) => esc(x.v)).filter(Boolean).join(' · ')}${(m.meta && m.meta.length) ? ' · ' : ''}generated ${today()}</div></div>
${(m.sections || []).map(sectionHtml).join('')}
</div></body></html>`;
}

export function exportReportHtml(m) {
  download(new Blob([buildReportHtml(m)], { type: 'text/html' }), `${safeName(m.title)}-${safeName(m.subtitle)}-${dateStamp()}.html`);
}
export function exportReportPdf(m) { printHtml(buildReportHtml(m)); }

/* ───── XLSX (SheetJS) ───── */
export function exportReportXlsx(m) {
  const wb = XLSX.utils.book_new();
  const used = new Set();
  const addSheet = (name, aoa) => {
    let n = String(name).replace(/[\\/?*[\]:]/g, ' ').slice(0, 28).trim() || 'Sheet';
    let base = n, i = 2; while (used.has(n.toLowerCase())) { n = `${base.slice(0, 25)} ${i++}`; }
    used.add(n.toLowerCase());
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), n);
  };
  const overview = [[m.title], [m.subtitle || ''], []];
  (m.meta || []).forEach((x) => overview.push([x.k, x.v]));
  (m.sections || []).forEach((s) => {
    if (s.kpis && s.kpis.length) { overview.push([]); overview.push([s.heading]); s.kpis.forEach((k) => overview.push([k.label, k.value])); }
    if (s.kv && s.kv.length) { overview.push([]); overview.push([s.heading]); s.kv.forEach((x) => overview.push([x.k, x.v])); }
  });
  addSheet('Overview', overview);
  (m.sections || []).forEach((s) => (s.tables || []).forEach((t) => {
    if (!t.rows || !t.rows.length) return;
    const aoa = [t.cols.map((c) => c.label), ...t.rows.map((r) => t.cols.map((c) => r[c.key] ?? ''))];
    addSheet(t.title || s.heading, aoa);
  }));
  XLSX.writeFile(wb, `${safeName(m.title)}-${safeName(m.subtitle)}-${dateStamp()}.xlsx`);
}

/* ───── backend (PPTX / DOCX) ───── */
export async function exportReportBackend(format, model, token) {
  // rasterize chart SVGs to PNG for the backend
  const m = JSON.parse(JSON.stringify(model));
  for (const s of (m.sections || [])) {
    for (const c of (s.charts || [])) {
      if (c.svg) { try { const png = await rasterizeSvg(c.svg); c.png = png.data; c.pw = png.w; c.ph = png.h; } catch { /* skip */ } delete c.svg; }
    }
  }
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${import.meta.env.VITE_API_URL}/api/nova/report-export`, {
    method: 'POST', headers, body: JSON.stringify({ format, model: m }),
  });
  if (!res.ok) throw new Error(`export ${format} failed (${res.status})`);
  download(await res.blob(), `${safeName(model.title)}-${safeName(model.subtitle)}-${dateStamp()}.${format}`);
}
